<template>
  <v-app id="inspire">
    <!-- Menú de navegación lateral (oculto en login) -->
    <v-navigation-drawer
      v-if="!isLoginRoute"
      :value="showDrawer"
      @input="(val) => (drawer = val)"
      app
      class="google-navigation"
      dark
    >
      <!-- Header del drawer -->
      <div class="g-drawer-header">
        <span>{{ this.$store.state.seller.PartyName }}</span>
      </div>

      <!-- Información del usuario -->
      <v-list-item class="google-user-item">
        <v-list-item-avatar color="primary">
          <span class="white--text text-button font-weight-medium">
            {{ getUserInitials }}
          </span>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title>{{ this.$store.state.profile.name }}</v-list-item-title>
          <v-list-item-subtitle>{{
            $store.getters.profile.user_access
          }}</v-list-item-subtitle>
          <v-list-item-subtitle>{{
            $store.getters.seller.currencyCode
          }}</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>

      <v-divider></v-divider>

      <!-- Menú principal -->
      <v-treeview
        return-object
        item-key="id"
        hoverable
        activatable
        :open="open"
        open-on-click
        @update:active="updateForm"
        @update:open="updateOpen"
        color="primary"
        :items="items"
        class="google-treeview"
      >
        <template v-slot:prepend="{ item, open }">
          <v-icon v-if="!item.icon">
            {{ open ? "mdi-folder-open" : "mdi-folder" }}
          </v-icon>
          <v-icon v-else :color="item.color || 'white'">
            {{ icons[item.icon] }}
          </v-icon>
        </template>
      </v-treeview>
      <v-divider></v-divider>
      <template v-slot:append>
        <v-list-item>
          <v-list-item-icon class="mr-2">
            <v-icon large color="grey">mdi-information-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="pa-0">
            <v-list-item-title>AI-BOS</v-list-item-title>
            <v-list-item-subtitle small>Business Operating Service</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-navigation-drawer>

    <!-- Barra superior (oculta en login) -->
    <v-app-bar v-if="!isLoginRoute" :value="$store.getters.getNavStatus" dense app flat>
      <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>
      <v-toolbar-title class="google-sans">
        {{ this.$store.state.seller.PartyName }} /
        {{ this.$store.state.seller.store_name }}
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-chip
        class="mr-2"
        small
        label
        :color="isOnline ? 'green' : 'red'"
        text-color="white"
      >
        <v-icon left small>{{ isOnline ? "mdi-wifi" : "mdi-wifi-off" }}</v-icon>
        {{ isOnline ? "ONLINE" : "OFFLINE" }}
      </v-chip>

      <v-chip class="google-time-chip" small outlined>
        {{ currentTime }}
      </v-chip>

      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on">
            <v-avatar size="32" color="primary">
              <span class="white--text text-button font-weight-medium">
                {{ getUserInitials }}
              </span>
            </v-avatar>
          </v-btn>
        </template>

        <v-card max-width="300">
          <v-list>
            <v-list-item>
              <v-list-item-avatar color="primary">
                <span class="white--text text-button font-weight-medium">
                  {{ getUserInitials }}
                </span>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{
                  this.$store.state.profile.name
                }}</v-list-item-title>
                <v-list-item-subtitle>{{
                  this.$store.state.profile.email
                }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <v-divider></v-divider>
          <v-card-actions>
            <v-btn text block @click="logout">
              <v-icon left small>mdi-logout</v-icon>
              Cerrar sesión
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </v-app-bar>

    <!-- Aviso global de red caída -->
    <div v-show="!isOnline" class="network-offline-banner">
      <v-icon color="white" small class="mr-2">mdi-wifi-off</v-icon>
      Sin conexión a internet. No se pueden guardar cambios hasta restablecer la red.
    </div>

    <!-- Área de contenido principal -->
    <v-main :class="{ 'login-main': isLoginRoute }">
      <transition name="fade" mode="out-in">
        <router-view></router-view>
      </transition>
    </v-main>

    <!-- Banner de Cookies GDPR -->
    <v-dialog
      v-model="cookieBanner"
      persistent
      max-width="900"
      :fullscreen="$vuetify.breakpoint.xsOnly"
    >
      <v-card class="cookie-card">
        <v-card-title class="text-h5 font-weight-bold pb-1">
          🍪 Configuración de Cookies
        </v-card-title>
        <v-card-text class="pb-2">
          <p class="mb-4">
            Utilizamos cookies para mejorar tu experiencia de navegación, analizar el
            tráfico del sitio y personalizar el contenido. Al hacer clic en "Aceptar
            todas", consientes el uso de todas las cookies.
          </p>
          <v-expansion-panels v-if="showDetails" flat class="cookie-panels">
            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-medium">
                Cookies Necesarias (Siempre activas)
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="text--secondary">
                  Estas cookies son esenciales para el funcionamiento del sitio web y no
                  se pueden desactivar.
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-medium">
                <v-checkbox
                  v-model="cookiePreferences.analytics"
                  @click.stop
                  hide-details
                  class="mt-0 pt-0"
                >
                  <template v-slot:label>
                    <span class="ml-2">Cookies Analíticas</span>
                  </template>
                </v-checkbox>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="text--secondary">
                  Nos ayudan a entender cómo los visitantes interactúan con el sitio web.
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-medium">
                <v-checkbox
                  v-model="cookiePreferences.marketing"
                  @click.stop
                  hide-details
                  class="mt-0 pt-0"
                >
                  <template v-slot:label>
                    <span class="ml-2">Cookies de Marketing</span>
                  </template>
                </v-checkbox>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="text--secondary">
                  Se utilizan para mostrar anuncios relevantes basados en tus intereses.
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <div class="mt-4">
            <a href="/politica-privacidad" target="_blank" class="text-decoration-none">
              Política de Privacidad
            </a>
            <span class="mx-2">|</span>
            <a href="/politica-cookies" target="_blank" class="text-decoration-none">
              Política de Cookies
            </a>
          </div>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-btn text @click="toggleDetails">
            {{ showDetails ? "Ocultar detalles" : "Personalizar" }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="rejectCookies">Rechazar todas</v-btn>
          <v-btn outlined color="black" @click="acceptSelected" v-if="showDetails">
            Guardar preferencias
          </v-btn>
          <v-btn color="black" dark @click="acceptAllCookies">Aceptar todas</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Banner simplificado -->
    <v-snackbar
      v-model="cookieSnackbar"
      :timeout="-1"
      bottom
      left
      color="white"
      elevation="8"
      max-width="500"
      v-if="!cookieBanner"
    >
      <div class="d-flex align-center">
        <div class="mr-4">
          <p class="mb-1 black--text">Utilizamos cookies para mejorar tu experiencia.</p>
          <a href="/politica-cookies" class="text-decoration-none text--secondary">
            Más información
          </a>
        </div>
        <v-spacer></v-spacer>
        <v-btn text color="grey" @click="rejectCookies">Rechazar</v-btn>
        <v-btn color="black" dark small @click="acceptAllCookies">Aceptar</v-btn>
      </div>
    </v-snackbar>

    <!-- Notificación de actualización -->
    <v-snackbar bottom right :value="updateExists" :timeout="-1" color="primary">
      Una actualización disponible.
      <template v-slot:action="{ attrs }">
        <v-btn text @click="refreshApp" v-bind="attrs">ACTUALIZAR</v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import update from "./mixins/update";
import { clearSession } from "@/services/sessionPersist";
import { isCashier, isRouteBlockedForCashier } from "@/utils/roles";

export default {
  data: () => ({
    currentTime: "",
    clockInterval: null,
    options: {
      timeZone: "America/Bogota",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false, // Use 24-hour format
    },
    // updateExists: false, // This should come from the mixin
    profile: { name: null },
    drawer: false, // CAMBIAR: Iniciar cerrado, se abrirá cuando navStatus sea true
    navStatus: false, // CAMBIAR: Iniciar como false hasta login
    open: [],

    // NUEVOS: Estados para cookies
    cookieBanner: false,
    cookieSnackbar: false,
    showDetails: false,
    cookiePreferences: {
      necessary: true,
      analytics: false,
      marketing: false,
    },

    icons: {
      project: "mdi-text-box-search-outline",
      users: "mdi-account-multiple-outline",
      weather: "mdi-weather-partly-snowy-rainy",
      crops: "mdi-sprout-outline",
      poly: "mdi-vector-polygon",
      dB: "mdi-database-cog-outline",
      chart: "mdi-chart-bell-curve-cumulative",
      clt: "mdi-account-circle-outline",
      set: "mdi-cog-transfer-outline",
      info: "mdi-book-information-variant",
      cale: "mdi-calendar-month-outline",
      invoice: "mdi-receipt-text-outline",
      invoicel: "mdi-invoice-list-outline",
      pedido: "mdi-file-document-edit-outline",
      truck: "mdi-truck-fast-outline",
      warehouse: "mdi-warehouse",
      inventory: "mdi-package-variant-closed",
      orders: "mdi-file-document-edit-outline",
      store: "mdi-store-outline",
      clock: "mdi-clock-check-outline",
      map: "mdi-map-marker-radius",
      home: "mdi-home-circle-outline",
      forklift: "mdi-forklift",
      cash: "mdi-cash-register",
      cloud: "mdi-cloud-search-outline",
      reports: "mdi-chart-line",
      grid: "mdi-view-grid-outline",
      ai: "mdi-head-snowflake-outline",
      vendor: "mdi-account-tie",
      supplier: "mdi-account-tie-outline",
      consecutivos: "mdi-format-list-numbered",
      vehicle: "mdi-car",
    },
    menu: {
      USUARIO: [
        {
          id: 1,
          name: "Documentos",
          icon: "cash",
          children: [
            {
              id: 11,
              name: "Factura Electrónica",
              icon: "invoice",
              to: "InvoiceE",
              billingMode: "formal",
            },
            {
              id: 14,
              name: "Facturas",
              icon: "invoice",
              to: "Invoices",
            },
            {
              id: 1201,
              name: "Apartados",
              icon: "invoice",
              to: "Apartados",
            },
            {
              id: 15,
              name: "Sistema POS",
              icon: "invoice",
              to: "invoices_pos",
            },
            {
              id: 16,
              name: "Notas Crédito",
              icon: "invoice",
              to: "CreditNotes",
            },
            {
              id: 17,
              name: "Registradora",
              icon: "invoice",
              to: "PLU",
              billingMode: "register",
            },
          ],
        },
        {
          id: 500,
          name: "Clientes",
          icon: "clt",
          color: "purple",
          children: [
            {
              id: 501,
              name: "Gestión de Clientes",
              to: "Clients",
              icon: "clt",
            },
          ],
        },
        {
          id: 700,
          name: "Caja",
          icon: "cash",
          children: [
            {
              id: 1001,
              name: "Comprobante de Ingreso",
              icon: "invoice",
              to: "Receipt_in",
            },
            {
              id: 1002,
              name: "Comprobante de Egreso",
              icon: "invoice",
              to: "Receipt_out",
            },
            {
              id: 21,
              name: "Reporte de Caja",
              icon: "cash",
              to: "Cash_report",
            },
            {
              id: 1000,
              name: "Reporte de Ventas",
              icon: "cash",
              to: "CashReports",
            },
          ],
        },
        {
          id: 99,
          name: "About us",
          icon: "info",
          to: "About",
        },
      ],
      ADMIN: [
        {
          id: 100,
          name: "Facturación",
          icon: "cash",
          color: "primary",
          children: [
            {
              id: 101,
              name: "Facturas",
              icon: "invoice",
              to: "Invoices",
            },
            {
              id: 1202,
              name: "Apartados",
              icon: "invoice",
              to: "Apartados",
            },
            {
              id: 110,
              name: "Registradora",
              icon: "cash",
              to: "/",
              billingMode: "register",
            },
            {
              id: 102,
              name: "Panel",
              icon: "cash",
              to: "InvoicePOS",
              billingMode: "restaurant",
            },
            {
              id: 109,
              name: "Panel Q",
              icon: "cash",
              to: "InvoiceQ",
              billingMode: "pos",
            },

            {
              id: 107,
              name: "Factura Electrónica",
              icon: "invoicel",
              to: "InvoiceE",
              billingMode: "formal",
            },
          ],
        },

        {
          id: 200,
          name: "Caja",
          icon: "cash",
          color: "warning",
          children: [
            {
              id: 203,
              name: "Reporte de Caja",
              icon: "cash",
              to: "CashDocs",
            },
            {
              id: 204,
              name: "Reporte de Ventas",
              icon: "invoice",
              to: "CashReports",
            },
          ],
        },

        {
          id: 400,
          name: "Inventario",
          icon: "inventory",
          color: "success",
          children: [
            {
              id: 401,
              name: "Buscar en tiendas",
              icon: "cloud",
              to: "G_search",
            },
            {
              id: 402,
              name: "Inventario",
              icon: "store",
              to: "Inventory",
            },

            {
              id: 403,
              name: "Ingresar Compra",
              icon: "store",
              to: "StockImport",
            },

            {
              id: 404,
              name: "Movimientos de Inventario *",
              icon: "store",
              to: "Events",
            },
          ],
        },
        // {
        //   id: 450,
        //   name: "Inspecciones Vehiculares",
        //   icon: "vehicle",
        //   color: "blue",
        //   children: [
        //     {
        //       id: 451,
        //       name: "Lista de Inspecciones",
        //       icon: "vehicle",
        //       to: "VehicleInspectionList",
        //     },
        //     {
        //       id: 452,
        //       name: "Nueva Inspección",
        //       icon: "vehicle",
        //       to: "VehicleInspection",
        //     },
        //   ],
        // },
        {
          id: 300,
          name: "Reportes",
          icon: "reports",
          children: [
            {
              id: 302,
              name: "Reporte de Caja",
              icon: "cash",
              to: "Cash_report",
            },

            {
              id: 304,
              name: "Resumen Diario",
              icon: "store",
              to: "Resume",
            },
            {
              id: 305,
              name: "Reporte de Informe diaro de caja",
              icon: "invoice",
              to: "DailyReport",
            },
            {
              id: 306,
              name: "Resumen Caja",
              icon: "reports",
              to: "Cash_report",
            },
          ],
        },
        {
          id: 6,
          name: "Compras",
          icon: "set",
          children: [
            {
              id: 61,
              name: "Ordenes de Compra",
              to: "Orders",
            },
            {
              id: 62,
              name: "Nueva Orden",
              to: "Order",
              icon: "orders",
            },
          ],
        },

        {
          id: 500,
          name: "Clientes",
          icon: "clt",
          color: "purple",
          children: [
            {
              id: 501,
              name: "Gestión de Clientes",
              to: "Clients",
              icon: "clt",
            },
          ],
        },

        {
          id: 5,
          name: "Configuracion",
          icon: "set",
          color: "warning",
          children: [
            {
              id: 54,
              name: "Perfil Compañía",
              to: "Company",
            },
            {
              id: 55,
              name: "Proveedores",
              to: "Proveedores",
              icon: "supplier",
              color: "blue",
            },
            {
              id: 57,
              name: "Settings",
              to: "Settings",
            },
            {
              id: 56,
              name: "Maestro",
              to: "Master",
            },
            {
              id: 51,
              name: "Vendedores",
              to: "Vendors",
              icon: "vendor",
            },
            {
              id: 58,
              name: "Usuarios",
              to: "Users",
              icon: "users",
            },
            {
              id: 52,
              name: "VendorsStat",
              to: "VendorsStat",
            },
            {
              id: 97,
              name: "AI-BOS - Doc Import",
              icon: "ai",
              to: "aiDocImport",
            },
            {
              id: 199,
              name: "AI-BOS - Master Import",
              icon: "ai",
              to: "aiMasterImport",
            },
          ],
        },

        {
          id: 707,
          name: "Auditoria",
          icon: "clt",
          color: "purple",
          children: [
            {
              id: 7071,
              name: "Auditoria",
              to: "AuditoriaInventario",
              icon: "inventory",
            },
            {
              id: 7072,
              name: "Auditoría en vivo",
              to: "AuditoriaEnVivo",
              icon: "inventory",
            },
          ],
        },

        {
          id: 900,
          name: "An\u00e1lisis",
          icon: "reports",
          color: "teal",
          children: [
            {
              id: 901,
              name: "Dashboard Tienda",
              icon: "store",
              to: "StoreDashboard",
            },
            {
              id: 905,
              name: "Ventas por producto",
              icon: "cash",
              to: "AnalystSales",
            },
            {
              id: 906,
              name: "Ventas e inventario",
              icon: "inventory",
              to: "AnalystSalesInv",
            },
            {
              id: 907,
              name: "Ventas por hora",
              icon: "clock",
              to: "AnalystHours",
            },
            {
              id: 902,
              name: "Análisis de ventas",
              icon: "cash",
              to: "SalesAnalysis",
            },
            {
              id: 903,
              name: "Salud de inventario",
              icon: "inventory",
              to: "InventoryAnalysis",
            },
            {
              id: 909,
              name: "Rotación",
              icon: "reports",
              to: "AnalystRotation",
            },
            {
              id: 910,
              name: "Precios y costos",
              icon: "reports",
              to: "AnalystPriceCost",
            },
            {
              id: 904,
              name: "Rentabilidad",
              icon: "cash",
              to: "ProfitAnalysis",
            },
            {
              id: 912,
              name: "Obsoletos por producto",
              icon: "inventory",
              to: "ObsoleteStock",
            },
            {
              id: 913,
              name: "Obsoletos por referencia",
              icon: "inventory",
              to: "ObsoleteRefe",
            },
          ],
        },

        {
          id: 99,
          name: "About us",
          icon: "info",
          to: "About",
        },
      ],
      Sysop: [
        {
          id: 100,
          name: "Facturación",
          icon: "cash",
          children: [
            {
              id: 101,
              name: "Facturas",
              icon: "invoice",
              to: "Invoices",
            },
            {
              id: 107,
              name: "Factura Electrónica",
              icon: "invoicel",
              to: "InvoiceE",
              billingMode: "formal",
            },
          ],
        },

        {
          id: 200,
          name: "Caja",
          icon: "cash",
          children: [
            {
              id: 203,
              name: "Documentos",
              icon: "invoice",
              to: "CashDocs",
            },
            {
              id: 204,
              name: "Reporte de Ventas",
              icon: "cash",
              to: "CashReports",
            },
          ],
        },

        {
          id: 400,
          name: "Inventario",
          icon: "inventory",
          children: [
            {
              id: 401,
              name: "Buscar en tiendas",
              icon: "cloud",
              to: "G_search",
            },
            {
              id: 402,
              name: "Inventario",
              icon: "store",
              to: "Inventory",
            },

            {
              id: 403,
              name: "Ingresar Compra",
              icon: "store",
              to: "StockImport",
            },

            {
              id: 404,
              name: "Movimientos de Inventario *",
              icon: "store",
              to: "Events",
            },
          ],
        },
        {
          id: 300,
          name: "Reportes",
          icon: "set",
          children: [
            {
              id: 302,
              name: "Reporte de Caja",
              icon: "cash",
              to: "Cash_report",
            },

            {
              id: 304,
              name: "Resumen Diario",
              icon: "store",
              to: "Resume",
            },
            {
              id: 305,
              name: "Reporte de Informe diaro de caja",
              icon: "invoice",
              to: "DailyReport",
            },
            {
              id: 306,
              name: "Resumen Caja",
              icon: "reports",
              to: "Cash_report",
            },
          ],
        },
        {
          id: 500,
          name: "Clientes",
          icon: "clt",
          color: "purple",
          children: [
            {
              id: 501,
              name: "Gestión de Clientes",
              to: "Clients",
              icon: "clt",
            },
          ],
        },
        {
          id: 5,
          name: "Opciones",
          icon: "set",
          children: [
            {
              id: 51,
              name: "Vendedores",
              to: "Vendors",
            },
            {
              id: 52,
              name: "VendorsStat",
              to: "VendorsStat",
            },
            {
              id: 54,
              name: "Perfil Compañía",
              to: "Company",
            },
            {
              id: 55,
              name: "API  Facturación Electrónica",
              to: "apiCompanies",
            },
            {
              id: 56,
              name: "apiSettings",
              to: "Configuracion API",
            },
            {
              id: 159,
              name: "Gestionar Consecutivos",
              to: "ConsecutivosManager",
              icon: "consecutivos",
              color: "orange",
            },
          ],
        },
        {
          id: 99,
          name: "About us",
          icon: "info",
          to: "About",
        },
      ],
      Auditor: [
        {
          id: 1,
          name: "Documentos",
          icon: "cash",
          children: [
            {
              id: 11,
              name: "Factura Electrónica",
              icon: "invoice",
              to: "InvoiceE",
              billingMode: "formal",
            },
            {
              id: 12,
              name: "Sistecredito",
              icon: "invoice",
              to: "Sistecredito",
            },
            {
              id: 14,
              name: "Facturas",
              icon: "invoice",
              to: "Invoices",
            },

            {
              id: 15,
              name: "Sistema POS",
              icon: "invoice",
              to: "invoices_pos",
            },
            {
              id: 16,
              name: "Notas Crédito",
              icon: "invoice",
              to: "CreditNotes",
            },
          ],
        },
        {
          id: 98,
          name: "Tienda",
          icon: "store",
          to: "inventory_status",
        },
        {
          id: 99,
          name: "About us",
          icon: "info",
          to: "About",
        },
      ],
      VENDEDOR: [
        {
          id: 1000,
          name: "Mi Panel",
          icon: "store",
          to: "Vendedor",
        },
        {
          id: 1,
          name: "Tienda",
          icon: "store",
          to: "inventory_status",
        },
        {
          id: 500,
          name: "Clientes",
          icon: "clt",
          color: "purple",
          children: [
            {
              id: 501,
              name: "Gestión de Clientes",
              to: "Clients",
              icon: "clt",
            },
          ],
        },
        {
          id: 99,
          name: "About us",
          icon: "info",
          to: "About",
        },
      ],
      StoreAdmin: [
        {
          id: 2900,
          name: "Análisis",
          icon: "reports",
          color: "teal",
          children: [
            { id: 2902, name: "Dashboard Tienda", icon: "store", to: "StoreDashboard" },
            { id: 2906, name: "Análisis de ventas", icon: "cash", to: "SalesAnalysis" },
            { id: 2907, name: "Salud de inventario", icon: "inventory", to: "InventoryAnalysis" },
            { id: 2903, name: "Ventas por producto", icon: "cash", to: "AnalystSales" },
            { id: 2908, name: "Ventas e inventario", icon: "inventory", to: "AnalystSalesInv" },
            { id: 2909, name: "Ventas por hora", icon: "clock", to: "AnalystHours" },
            { id: 2904, name: "Rotación", icon: "reports", to: "AnalystRotation" },
            { id: 2910, name: "Rentabilidad", icon: "cash", to: "ProfitAnalysis" },
          ],
        },
        {
          id: 3000,
          name: "Configuracion",
          icon: "set",
          color: "warning",
          children: [
            {
              id: 3001,
              name: "Perfil Compania",
              icon: "store",
              to: "Company",
            },
            {
              id: 3002,
              name: "Settings",
              icon: "set",
              to: "Settings",
            },
            {
              id: 3003,
              name: "Gestionar Consecutivos",
              icon: "consecutivos",
              color: "orange",
              to: "ConsecutivosManager",
            },
            {
              id: 3004,
              name: "API Facturacion Electronica",
              icon: "invoice",
              to: "apiCompanies",
            },
            {
              id: 3005,
              name: "Configuracion API",
              icon: "set",
              to: "apiSettings",
            },
          ],
        },
        {
          id: 3100,
          name: "Personal",
          icon: "clt",
          color: "primary",
          children: [
            {
              id: 3101,
              name: "Usuarios",
              icon: "clt",
              to: "Users",
            },
            {
              id: 3102,
              name: "Vendedores",
              icon: "vendor",
              to: "Vendors",
            },
            {
              id: 3103,
              name: "Estadisticas Vendedores",
              icon: "chart",
              to: "VendorsStat",
            },
          ],
        },
        {
          id: 3200,
          name: "Datos Maestros",
          icon: "dB",
          color: "success",
          children: [
            {
              id: 3201,
              name: "Maestro de Productos",
              icon: "inventory",
              to: "Master",
            },
            {
              id: 3202,
              name: "Proveedores",
              icon: "supplier",
              color: "blue",
              to: "Proveedores",
            },
            {
              id: 3203,
              name: "Clientes",
              icon: "clt",
              to: "Clients",
            },
          ],
        },
        {
          id: 3300,
          name: "Herramientas",
          icon: "ai",
          color: "purple",
          children: [
            {
              id: 3302,
              name: "Importar Documentos",
              icon: "ai",
              to: "aiDocImport",
            },
            {
              id: 3303,
              name: "Importar Maestro",
              icon: "ai",
              to: "aiMasterImport",
            },
          ],
        },
        {
          id: 3999,
          name: "About us",
          icon: "info",
          to: "About",
        },
      ],
      Analista: [
        {
          id: 2000,
          name: "Inicio",
          icon: "grid",
          color: "primary",
          to: "AnalystDashboard",
        },
        {
          id: 2400,
          name: "Ventas",
          icon: "cash",
          color: "success",
          children: [
            { id: 2401, name: "Ventas por producto", icon: "cash", to: "AnalystSales" },
            { id: 2402, name: "Ventas e inventario", icon: "inventory", to: "AnalystSalesInv" },
            { id: 2403, name: "Ventas por hora", icon: "clock", to: "AnalystHours" },
            { id: 2404, name: "Análisis de ventas", icon: "reports", to: "SalesAnalysis" },
          ],
        },
        {
          id: 2500,
          name: "Inventario",
          icon: "inventory",
          color: "teal",
          children: [
            { id: 2504, name: "Existencias cadena", icon: "inventory", to: "AnalystInventory" },
            { id: 2502, name: "Salud de inventario", icon: "inventory", to: "InventoryAnalysis" },
            { id: 2505, name: "Obsoletos por tienda", icon: "inventory", to: "ObsoleteStocks" },
            { id: 2506, name: "Obsoletos por producto", icon: "inventory", to: "ObsoleteStock" },
            { id: 2507, name: "Obsoletos por referencia", icon: "inventory", to: "ObsoleteRefe" },
          ],
        },
        {
          id: 2550,
          name: "Rotación y margen",
          icon: "reports",
          color: "orange",
          children: [
            { id: 2551, name: "Rotación", icon: "reports", to: "AnalystRotation" },
            { id: 2552, name: "Precios y costos", icon: "cash", to: "AnalystPriceCost" },
            { id: 2503, name: "Rentabilidad", icon: "cash", to: "ProfitAnalysis" },
          ],
        },
        {
          id: 2001,
          name: "Dashboard Tienda",
          icon: "store",
          color: "primary",
          to: "StoreDashboard",
        },
        {
          id: 2600,
          name: "AI-BOS",
          icon: "ai",
          color: "deep-orange",
          to: "aibos",
        },
        {
          id: 2100,
          name: "Operativo",
          icon: "store",
          color: "warning",
          children: [
            {
              id: 2101,
              name: "Reporte de Caja",
              icon: "cash",
              to: "Cash_report",
            },
            {
              id: 2102,
              name: "Facturas",
              icon: "invoice",
              to: "Invoices",
            },
            {
              id: 2103,
              name: "Buscar Productos",
              icon: "cloud",
              to: "G_search",
            },
          ],
        },
        {
          id: 2700,
          name: "Compras",
          icon: "supplier",
          color: "blue",
          children: [
            {
              id: 2701,
              name: "Ordenes de Compra",
              icon: "invoice",
              to: "Orders",
            },
            {
              id: 2702,
              name: "Nueva Orden",
              icon: "cash",
              to: "Order",
            },
            {
              id: 2703,
              name: "Proveedores",
              icon: "supplier",
              to: "Proveedores",
            },
          ],
        },
        {
          id: 2999,
          name: "About us",
          icon: "info",
          to: "About",
        },
      ],
    },
  }),

  mounted() {
    this.updateTime();
    this.clockInterval = setInterval(this.updateTime, 1000);

    if (this.$store.state.profile && this.$store.state.profile.token) {
      this.drawer = true;
      this.$store.dispatch("setNavStatus", true);
      this.$store.dispatch("set_menu", true);
    }

    // NUEVO: Verificar consentimiento de cookies al cargar la app
    this.checkCookieConsent();
  },

  beforeDestroy() {
    clearInterval(this.clockInterval);
  },

  computed: {
    // Estado de red global (true = hay conexión)
    isOnline() {
      return this.$store.getters.isOnline;
    },

    showDrawer() {
      // El drawer solo debe mostrarse si:
      // 1. El estado de navegación está activo (navStatus !== false)
      // 2. El usuario quiere ver el menú (getMenuStatus = true)
      // 3. El drawer local está abierto (drawer = true)
      const menuStatus = this.$store.getters.getMenuStatus;
      const navActive = this.$store.state.navStatus !== false;

      console.log("🧐 showDrawer computed:", {
        menuStatus,
        navActive,
        drawer: this.drawer,
        result: menuStatus && navActive && this.drawer,
      });

      return menuStatus && navActive && this.drawer;
    },

    // Detectar si estamos en la vista de Login para ocultar layout principal
    isLoginRoute() {
      // Nombre de la ruta de login según router: 'Login'
      return (
        this.$route && this.$route.name && this.$route.name.toLowerCase() === "login"
      );
    },

    items() {
      const userAccess = this.$store.getters.profile.user_access || "USUARIO";
      const menuKey = userAccess === "ANALISTA" ? "Analista" : userAccess;
      const baseMenu = this.menu[menuKey] || this.menu["USUARIO"];
      const storeStatus =
        window.store && window.store.status ? window.store.status : null;
      const billingMode = this.$store.state.billingMode || "register";

      const modifiedMenu = JSON.parse(JSON.stringify(baseMenu));

      const group = modifiedMenu.find(
        (item) => item.name === "Facturación" || item.name === "Documentos"
      );

      if (group && group.children) {
        const facturaElectronica = group.children.find(
          (child) => child.name === "Factura Electrónica"
        );

        if (facturaElectronica) {
          switch (storeStatus) {
            case "Register":
            case "PLU":
            case "EAN":
            default:
              facturaElectronica.to = "InvoiceE";
          }
        }
      }

      modifiedMenu.forEach((g) => {
        if (g.children) {
          g.children = g.children.filter(
            (c) => !c.billingMode || c.billingMode === billingMode
          );
        }
      });

      // Cajero: ocultar Reporte de Caja y Reporte de Ventas
      if (isCashier(this.$store.getters.profile)) {
        modifiedMenu.forEach((g) => {
          if (g.children) {
            g.children = g.children.filter((c) => !isRouteBlockedForCashier(c.to));
          }
        });
      }

      return modifiedMenu.filter((g) => !g.children || g.children.length > 0);
    },

    // Obtener iniciales del usuario para el avatar
    getUserInitials() {
      const name = this.$store.state.profile.name || "";
      if (!name) return "?";

      return name
        .split(" ")
        .map((part) => part.charAt(0).toUpperCase())
        .slice(0, 2)
        .join("");
    },
  },

  watch: {
    "$store.getters.getMenuStatus"(newVal) {
      console.log("📄 MenuStatus changed:", newVal);

      // Solo mostrar drawer si navStatus es true Y el usuario quiere abrirlo
      if (newVal && this.$store.state.navStatus !== false) {
        this.drawer = true;
      } else {
        this.drawer = false;
      }
    },

    // NUEVO: Watcher para rutas
    $route(to) {
      // Verificar cookies en cada cambio de ruta (excepto login)
      // Normalizamos el nombre porque en el router se definió como 'Login'
      const isLogin = to && to.name && to.name.toLowerCase() === "login";
      if (!isLogin) {
        this.checkCookieConsent();
      }
    },
  },

  mixins: [update],

  methods: {
    updateTime() {
      const date = new Date();
      const madridTime = date
        .toLocaleString("en-US", this.options)
        .replace(/(\d+)\/(\d+)\/(\d+), (\d+:\d+:\d+)/, "$3-$1-$2 $4");

      this.currentTime = madridTime;
    },

    // refreshApp is now provided by the update mixin

    updateForm(item) {
      if (item.length > 0 && item[0].to) {
        if (item[0].to === "Vendedor") {
          window.location.assign("/android/");
          return;
        }
        this.$router.push(item[0].to);
      }
    },

    updateOpen(openItems) {
      this.open = openItems.length ? [openItems[openItems.length - 1]] : [];
    },

    logout() {
      console.log("🚪 Iniciando logout...");

      // Ocultar drawer inmediatamente
      this.drawer = false;

      // Establecer estados de navegación
      this.$store.state.navStatus = false;

      // Limpiar datos del store
      this.$store.state.profile = { name: null, token: null };
      this.$store.dispatch("set_menu", false);
      clearSession();

      console.log("✅ Logout completado - navegando a login");

      // Navegar a login (por nombre) para garantizar que muestra LoginFrom
      this.$router.push({ name: "Login" });
    },

    // NUEVOS MÉTODOS PARA COOKIES
    checkCookieConsent() {
      const consent = localStorage.getItem("cookieConsent");
      if (consent) {
        this.cookieBanner = false;
        this.cookieSnackbar = false;
        const preferences = JSON.parse(consent);
        this.cookiePreferences = { ...this.cookiePreferences, ...preferences };

        // Aplicar las preferencias guardadas
        if (preferences.analytics) {
          this.enableAnalytics();
        }
        if (preferences.marketing) {
          this.enableMarketing();
        }
      } else {
        // Solo mostrar en ciertas rutas (no en login)
        if (this.$route.name !== "login") {
          this.cookieSnackbar = true;
        }
      }
    },

    toggleDetails() {
      this.showDetails = !this.showDetails;
      if (this.showDetails) {
        this.cookieBanner = true;
        this.cookieSnackbar = false;
      }
    },

    acceptAllCookies() {
      const preferences = {
        necessary: true,
        analytics: true,
        marketing: true,
        timestamp: new Date().toISOString(),
      };

      localStorage.setItem("cookieConsent", JSON.stringify(preferences));
      this.cookiePreferences = preferences;
      this.cookieBanner = false;
      this.cookieSnackbar = false;

      this.enableAnalytics();
      this.enableMarketing();

      console.log("✅ Todas las cookies aceptadas");
    },

    acceptSelected() {
      const preferences = {
        necessary: true,
        analytics: this.cookiePreferences.analytics,
        marketing: this.cookiePreferences.marketing,
        timestamp: new Date().toISOString(),
      };

      localStorage.setItem("cookieConsent", JSON.stringify(preferences));
      this.cookieBanner = false;
      this.cookieSnackbar = false;

      if (preferences.analytics) {
        this.enableAnalytics();
      }
      if (preferences.marketing) {
        this.enableMarketing();
      }

      console.log("✅ Preferencias guardadas:", preferences);
    },

    rejectCookies() {
      const preferences = {
        necessary: true,
        analytics: false,
        marketing: false,
        timestamp: new Date().toISOString(),
      };

      localStorage.setItem("cookieConsent", JSON.stringify(preferences));
      this.cookiePreferences = preferences;
      this.cookieBanner = false;
      this.cookieSnackbar = false;

      console.log("❌ Cookies rechazadas");
    },

    enableAnalytics() {
      console.log("📊 Analytics habilitado");

      // Google Analytics 4
      if (typeof window.gtag !== "undefined") {
        window.gtag("consent", "update", {
          analytics_storage: "granted",
        });
      }
    },

    enableMarketing() {
      console.log("📢 Marketing habilitado");

      // Google Analytics 4
      if (typeof window.gtag !== "undefined") {
        window.gtag("consent", "update", {
          ad_storage: "granted",
        });
      }
    },

    // Método útil para desarrollo
    resetCookieConsent() {
      localStorage.removeItem("cookieConsent");
      this.cookieBanner = false;
      this.cookieSnackbar = true;
      this.showDetails = false;
      this.cookiePreferences = {
        necessary: true,
        analytics: false,
        marketing: false,
      };
      console.log("🔄 Consentimiento de cookies reseteado");
    },
  },
};
</script>

<style>
/* Banner global de red caída */
.network-offline-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  background-color: #d32f2f;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Solo unos pocos estilos específicos que no se pueden lograr con el tema global */
.g-drawer-header {
  padding: 16px;
  font-size: 20px;
  font-weight: 500;
  color: white;
}

.google-user-item {
  background-color: var(--v-grey-background);
  margin: 8px;
  border-radius: 8px;
}

.google-time-chip {
  margin-right: 16px;
  height: 32px !important;
}

.google-treeview >>> .v-treeview-node__root {
  border-radius: 0 20px 20px 0;
  margin-right: 8px;
}

.google-treeview >>> .v-treeview-node--active > .v-treeview-node__root {
  background-color: var(--v-primary-lighten);
}

.google-navigation {
  border-right: none !important;
}

/* Corrección para centrar texto en avatares */
.v-avatar {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.v-avatar span {
  line-height: 1 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

/* Animación de transición entre páginas */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* NUEVOS ESTILOS PARA COOKIES */
.cookie-card {
  border-radius: 12px !important;
}

.cookie-panels {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.cookie-panels .v-expansion-panel {
  background: transparent !important;
}

.cookie-panels .v-expansion-panel-header {
  min-height: 56px;
  padding: 12px 16px;
}

.v-snackbar__wrapper {
  border-radius: 12px !important;
}

.v-snackbar__content {
  padding: 20px 24px !important;
}

/* Ajustes específicos para el main del login (sin barras) */
.login-main {
  padding: 0 !important;
  background: #ffffff;
}
</style>
